// Card 07, the current field (R2616, R2716; ui-map-interface.md §17.3.07,
// v2-the-deep.md §5, §13; mockup round 117).
//
// The piece draws the current of each cell in the look of the World field
// `currents` (themes.md §3.4). The ten looks are the owner's picks of
// round 117: card 09 Quiet drift (the AUTO look), 01 Full flow, 02
// Streaks, 04 Field and streaks, 06 Long flowlines, 08 Fast water, 10 Dusk
// field, 15 Plankton motes, 17 Stream atlas and 19 Ripple chevrons. Every
// figure of every look is a column of `[chart.piece.current_arrows]`.
//
// Three kinds of mark draw a look:
//
//   - **Streaks** (cards 01 to 10, 15): `current-streaks.ts`, stepped on
//     every frame. Cards 01, 04, 08 and 10 lay the speed field under them:
//     a smooth wash in the cold ramp, ONE full-world quad of the speed
//     field layer (`fast/layers/speedfield.ts`, R2827). The piece hands the
//     flow grid to the layer through the scene, and draws no batch for it.
//   - **The stream atlas** (card 17): still arrows on a lattice whose
//     weight gives the speed, and the rate of the strongest beside them.
//   - **Ripple chevrons** (card 19): a chevron on each node of a world
//     lattice slides a little down the current, fades and comes back.
//
// The slug stays `current_arrows`: a stored Chart theme and the VIEW group
// `currents` name the piece by it (R2716).
//
// **THE TIDE SCALES IT.** The field of the hello is the current at tide
// factor 1, and the factor of the tide step now (`GameState.tideFactor`)
// scales every mark; a negative factor turns it round.
//
// **IT DRAWS WHAT THE WIRE SENDS.** The field is `Hello.currents` (R2609),
// nil while the flag `currents` is off, so a world with the flag off draws
// nothing. The piece reads no flag (§14.1 rule 5).
//
// **IT PAUSES WITH THE MAP.** A live piece steps only while the chart asks
// it, and the loop asks nothing while the page is hidden (§10.4). Under
// reduced motion every look draws its still form.

import { CURRENT_HEAT_STYLES, CURRENT_STYLES } from '../../../gen/balance';
import { CHART } from '../../../gen/presentation';
import { currentAt, fieldCellsSince, fieldRev, strongestCurrent } from '../../../deep';
import { reducedMotion } from '../../../render/motion';
import { COLS, ROWS, cellCenterX, cellCenterY } from '../../../world';
import { F_ROUND, F_SCREEN, type FrameInfo, type ViewRect } from '../../layer';
import type { CurrentLook } from '../../../render/scene-look';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { CHART_INK, faceOf, inkOf } from './shared';
import { FlowGridPatch, buildFlowGrid, flowAt, strideFor, type FlowGrid } from './current-field';
import { Streaks, streakCount, streakLook, type StreakEnv, type StreakLook } from './current-streaks';
import { BandGains } from './current-depth';
import { HeatmapKind } from '../../../gen/proto/aquaphobia';

/** The figures of the piece's own table. */
const T = CHART.pieces.current_arrows;

/** The angle of an arrow head stroke off the shaft. */
const HEAD_RAD = Math.PI / 6;

/**
 * Every current look, the base looks then the heat looks (R2869). Each
 * `.arr` column of `[chart.piece.current_arrows]` stands in this order.
 */
export const CURRENT_LOOKS: readonly CurrentLook[] = [...CURRENT_STYLES, ...CURRENT_HEAT_STYLES];

/** The index of a look in `CURRENT_LOOKS`; AUTO for an unknown id. */
export function lookIndex(style: string | undefined): number {
  const i = (CURRENT_LOOKS as readonly string[]).indexOf(style ?? '');
  return i < 0 ? 0 : i;
}

/** One stream atlas arrow: its centre, its unit way, its band and rate. */
export interface AtlasArrow { x: number; y: number; ux: number; uy: number; band: number; pxH: number }

/** True where a mark of reach `r` round a world point stands off the rect. */
function off(v: ViewRect, x: number, y: number, r: number): boolean {
  return x + r < v.x0 || x - r > v.x1 || y + r < v.y0 || y - r > v.y1;
}

/** A small integer hash of a lattice node, 0 to 1. */
function hash01(i: number, j: number, salt: number): number {
  let h = Math.imul(i, 0x27d4eb2d) ^ Math.imul(j, 0x165667b1) ^ Math.imul(salt + 1, 0x9e3779b9);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

export class CurrentFlowPiece implements Piece {
  readonly id = 'current_arrows' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'live' as const;
  /** The tide is the season running now: a replay film hides the field. */
  readonly film = 'hide' as const;
  readonly rebuildOn = ['hello', 'survey', 'look'] as const;

  /** The resampled field, and what it was built from. */
  grid: FlowGrid | null = null;
  private gridOf: Int8Array | null = null;
  private gridRev = -1;
  private gridCols = 0;
  private gridRows = 0;
  private gridBlocked: Uint8Array | null = null;
  private gridUnit = 0;
  private gridStrongest = 0;
  /** The in-place resample of a survey pass (R2897). */
  private readonly patch = new FlowGridPatch();

  /** The look drawn now, as its index in `CURRENT_LOOKS`. */
  look = 0;
  /** Reduced motion, read at a build and not on every frame. */
  still = false;
  /** The streaks of the look, or null for a look with none. */
  streaks: Streaks | null = null;
  /** What the streaks were seeded for: the look, the count and the motion
   *  setting. Three figures and no string key, so a frame allocates nothing
   *  (§10.3); a count of -1 seeds them again on the next update. */
  private streakOf = -1;
  private streakN = -1;
  private streakStill = false;
  /** The streak figures of one look, read again for a new look alone. */
  private figs: StreakLook | null = null;
  private figsOf = -1;

  /** The stream atlas arrows, and the tide and the stride they were packed
   *  for; a stride of -1 packs them again. */
  atlas: AtlasArrow[] = [];
  private atlasTide = 0;
  private atlasStride = -1;

  private readonly env: StreakEnv = {
    grid: null as unknown as FlowGrid, tide: 1, strongest: 0, zoom: 1,
    view: { x0: 0, y0: 0, x1: 0, y1: 0 },
  };
  private readonly cur: [number, number] = [0, 0];

  /** The alpha gain of each depth band while the tints draw (R2871). */
  private readonly gains = new BandGains();
  /** The full alpha of the marks of a look, and the look it is of. */
  private alphaLook = -1;
  private alphaOfLook = 0;

  /** The full alpha of the marks of a look, which the gains are solved
   *  at: the streak alpha, or the arrow alpha of the stream atlas. */
  private markAlpha(look: number): number {
    if (look !== this.alphaLook) {
      this.alphaLook = look;
      this.alphaOfLook = CURRENT_LOOKS[look] === 'stream-atlas' ? T.fig.atlas_alpha : this.figures(look).alpha;
    }
    return this.alphaOfLook;
  }

  /** The streak figures of a look, built once a look. */
  private figures(look: number): StreakLook {
    if (look !== this.figsOf || this.figs === null) {
      this.figsOf = look;
      this.figs = streakLook(look);
    }
    return this.figs;
  }

  /** The DEPTH heat map hides the piece, so the streaks and the speed
   *  field do not draw over the depth heat look (R2870). */
  visible(ctx: ChartContext): boolean {
    return ctx.state.currents !== null && ctx.state.heatmap !== HeatmapKind.HEATMAP_KIND_DEPTH;
  }

  /**
   * A new hello, a survey pass or a new look. The streaks seed again for a
   * new look, a new motion setting or a grid built whole; a survey pass
   * patches the grid, and the streaks run on over it (R2897).
   */
  rebuild(ctx: ChartContext): void {
    this.still = reducedMotion();
    this.look = lookIndex(ctx.scene.currentLook);
    this.atlasStride = -1;
    this.syncGrid(ctx);
  }

  /**
   * The field on the grid, built whole for a new field, a full frame or a
   * new grid. A survey pass patches the nodes round the cells it wrote
   * (R2897): the change log of `deep.ts` names them. A moved grid goes to
   * the speed field layer at once (R2827): the texture is written again
   * then, the rows of the patch alone while the strongest current holds,
   * and on no tide step.
   */
  private syncGrid(ctx: ChartContext): FlowGrid | null {
    const field = ctx.state.currents;
    if (!field) {
      if (this.gridOf !== null) ctx.scene.setSpeedField?.(null, 0);
      this.grid = null;
      this.gridOf = null;
      return null;
    }
    const rev = fieldRev();
    const blocked = ctx.scene.blocked;
    const unit = ctx.state.rules.currentUnitPxH;
    const same = field === this.gridOf && COLS === this.gridCols && ROWS === this.gridRows
      && blocked === this.gridBlocked && unit === this.gridUnit;
    if (same && rev === this.gridRev) return this.grid;
    const strongest = strongestCurrent(field, unit);
    const grid = this.grid;
    if (same && grid) {
      const patch = this.patch;
      patch.begin(grid, field, unit, COLS, ROWS, blocked);
      if (fieldCellsSince(this.gridRev, patch.visit)) {
        this.gridRev = rev;
        if (patch.j1 < patch.j0) return grid;
        // The streaks read the grid in place; the stream atlas packs again.
        this.atlasStride = -1;
        if (strongest === this.gridStrongest && ctx.scene.patchSpeedField) {
          ctx.scene.patchSpeedField(grid, strongest, patch);
        } else {
          ctx.scene.setSpeedField?.(grid, strongest);
        }
        this.gridStrongest = strongest;
        return grid;
      }
    }
    this.grid = buildFlowGrid(field, unit, COLS, ROWS, blocked);
    this.gridOf = field;
    this.gridRev = rev;
    this.gridCols = COLS;
    this.gridRows = ROWS;
    this.gridBlocked = blocked;
    this.gridUnit = unit;
    this.gridStrongest = strongest;
    this.streakN = -1;
    this.atlasStride = -1;
    ctx.scene.setSpeedField?.(this.grid, strongest);
    return this.grid;
  }

  /** The world the streaks read this frame. */
  private envOf(ctx: ChartContext, view: StreakEnv['view'], grid: FlowGrid): StreakEnv {
    const e = this.env;
    e.grid = grid;
    e.tide = ctx.state.tideFactor();
    e.strongest = strongestCurrent(ctx.state.currents, ctx.state.rules.currentUnitPxH);
    e.zoom = ctx.zoom;
    e.view = view;
    return e;
  }

  update(ctx: ChartContext, frame: FrameInfo): void {
    const grid = this.syncGrid(ctx);
    const look = lookIndex(ctx.scene.currentLook);
    if (look !== this.look) { this.look = look; this.streakN = -1; }
    if (!grid) { this.streaks = null; return; }
    const env = this.envOf(ctx, frame.view, grid);
    const sl = this.figures(look);
    const w = Math.round((frame.view.x1 - frame.view.x0) * ctx.zoom);
    const h = Math.round((frame.view.y1 - frame.view.y0) * ctx.zoom);
    const n = streakCount(sl, w * h, ctx.sheet);
    if (look !== this.streakOf || n !== this.streakN || this.still !== this.streakStill) {
      this.streakOf = look;
      this.streakN = n;
      this.streakStill = this.still;
      this.streaks = n > 0 ? new Streaks(sl, n, this.still) : null;
      this.streaks?.seed(env);
      return;
    }
    this.streaks?.step(Math.min(0.1, frame.dtMs / 1000), env);
  }

  emit(sinks: ChartSinks, ctx: ChartContext, frame: FrameInfo): void {
    const grid = this.grid;
    if (!grid || !ctx.state.currents) return;
    const r = T[ctx.reading];
    const ink = inkOf(r.ink, CHART_INK.pale);
    const env = this.envOf(ctx, frame.view, grid);
    const look = this.look;
    // The streaks follow the depth bands while the tints draw (R2871): the
    // scene names the look the tints draw in, null while none draws.
    const gains = this.gains;
    gains.set(ctx.state.depthBands, ctx.scene.tintLook ?? null, ctx.reading, ink, this.markAlpha(look) * r.alpha);
    this.streaks?.emit(sinks.segments, sinks.circles, ink, frame.timeS, r.alpha, gains);
    const style = CURRENT_LOOKS[look];
    if (style === 'stream-atlas') this.emitAtlas(sinks, ctx, env, ink, r.alpha);
    if (style === 'ripple-chevrons') this.emitChevrons(sinks, ctx, env, ink, r.alpha, frame.timeS);
  }

  // -- card 17, the stream atlas -----------------------------------------------

  /** The arrows of the lattice, packed again for a new tide, field or band. */
  private atlasArrows(ctx: ChartContext, env: StreakEnv, stride: number): AtlasArrow[] {
    const field = ctx.state.currents;
    if ((env.tide === this.atlasTide && stride === this.atlasStride) || !field) return this.atlas;
    this.atlasTide = env.tide;
    this.atlasStride = stride;
    const out: AtlasArrow[] = [];
    const f = T.fig, half = stride >> 1, unit = ctx.state.rules.currentUnitPxH;
    const sign = env.tide < 0 ? -1 : 1;
    const bands = Math.max(1, Math.round(f.atlas_bands));
    for (let row = half; row < ROWS; row += stride) {
      for (let col = half; col < COLS; col += stride) {
        const c = row * COLS + col;
        const [vx, vy] = currentAt(field, c, unit, this.cur);
        const raw = Math.hypot(vx, vy);
        const s = raw * Math.abs(env.tide);
        if (!(s >= f.atlas_min_px_h) || raw === 0) continue;
        const u = env.strongest > 0 ? s / env.strongest : 0;
        out.push({
          x: cellCenterX(c), y: cellCenterY(c), ux: (sign * vx) / raw, uy: (sign * vy) / raw,
          band: Math.min(bands - 1, Math.floor(u * f.atlas_band_gain)), pxH: s,
        });
      }
    }
    this.atlas = out;
    return out;
  }

  private emitAtlas(sinks: ChartSinks, ctx: ChartContext, env: StreakEnv, ink: number, scale: number): void {
    const f = T.fig, z = Math.max(1e-6, ctx.zoom);
    const arrows = this.atlasArrows(ctx, env, strideFor(f.atlas_gap_px, z));
    const len = f.atlas_len_px / z, a0 = f.atlas_alpha * scale;
    const ch = Math.cos(HEAD_RAD), sh = Math.sin(HEAD_RAD);
    const seg = sinks.segments, flags = F_SCREEN | F_ROUND;
    const face = faceOf(ctx.reading);
    for (const ar of arrows) {
      if (off(ctx.cull, ar.x, ar.y, len)) continue;
      // The arrow follows the depth band under it (R2871).
      const g = this.gains.at(ar.x, ar.y), a = a0 * g;
      const w = f.atlas_width_px + ar.band * f.atlas_width_step_px;
      const head = (f.atlas_head_px + ar.band * f.atlas_head_step_px) / z;
      const hx = ar.x + (ar.ux * len) / 2, hy = ar.y + (ar.uy * len) / 2;
      seg.line(ar.x - (ar.ux * len) / 2, ar.y - (ar.uy * len) / 2, hx, hy, w, ink, a, flags);
      const bx = -ar.ux, by = -ar.uy;
      seg.line(hx, hy, hx + head * (bx * ch - by * sh), hy + head * (bx * sh + by * ch), w, ink, a, flags);
      seg.line(hx, hy, hx + head * (bx * ch + by * sh), hy + head * (-bx * sh + by * ch), w, ink, a, flags);
      if (ar.pxH < f.atlas_rate_px_h) continue;
      // The rate in px a wall minute, the unit of the legend card (§17.3.07).
      const n = Math.round((ar.pxH * ctx.state.tacticalScale) / 6) / 10;
      const lift = f.atlas_rate_off_px / z;
      ctx.glyphs.word(sinks.quads, String(n), ar.x - ar.uy * lift, ar.y + ar.ux * lift,
        face, T.text.rate, ink, f.atlas_rate_alpha * scale * g, { anchor: 'centre' });
    }
  }

  // -- card 19, the ripple chevrons --------------------------------------------

  private emitChevrons(
    sinks: ChartSinks, ctx: ChartContext, env: StreakEnv, ink: number, scale: number, t: number,
  ): void {
    const f = T.fig, z = Math.max(1e-6, ctx.zoom), grid = env.grid;
    // The lattice is a world lattice, so a chevron keeps its water under a
    // pan; its step is the screen gap at the nearest power-of-two zoom band.
    const band = 2 ** Math.round(Math.log2(z));
    const gap = f.chev_gap_px / band, jit = f.chev_jitter_px / band;
    const v = ctx.cull;
    const i0 = Math.floor(v.x0 / gap), i1 = Math.ceil(v.x1 / gap);
    const j0 = Math.floor(v.y0 / gap), j1 = Math.ceil(v.y1 / gap);
    const sign = env.tide < 0 ? -1 : 1, levels = Math.max(1, Math.round(f.chev_levels));
    const cur = this.cur, flags = F_SCREEN | F_ROUND, seg = sinks.segments;
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const x = (i + 0.5) * gap + (hash01(i, j, 1) - 0.5) * jit;
        const y = (j + 0.5) * gap + (hash01(i, j, 2) - 0.5) * jit;
        if (!flowAt(grid, x, y, cur)) continue;
        const s = Math.hypot(cur[0], cur[1]);
        if (!(s >= f.chev_min_px_h)) continue;
        const u = env.strongest > 0 ? Math.min(1, (s * Math.abs(env.tide)) / env.strongest) : 0;
        const ph = this.still ? 0.5 : (hash01(i, j, 3) + t * (f.chev_rate + f.chev_rate_gain * u)) % 1;
        const a = Math.sin(ph * Math.PI) * (f.chev_floor + (1 - f.chev_floor) * Math.min(1, u * f.chev_gain));
        if (a < f.chev_cut) continue;
        const l = Math.min(levels - 1, Math.floor(a * levels));
        const alpha = (f.chev_alpha + f.chev_alpha_step * l) * scale;
        const ux = (sign * cur[0]) / s, uy = (sign * cur[1]) / s;
        const slide = ((ph - 0.5) * (f.chev_slide_px + f.chev_slide_gain_px * u)) / z;
        const size = (f.chev_size_px + f.chev_size_gain_px * u) / z, wing = size * f.chev_wing;
        const cx = x + ux * slide, cy = y + uy * slide;
        const px = -uy, py = ux;
        seg.line(cx - ux * size + px * wing, cy - uy * size + py * wing, cx, cy, f.chev_width_px, ink, alpha, flags);
        seg.line(cx, cy, cx - ux * size - px * wing, cy - uy * size - py * wing, f.chev_width_px, ink, alpha, flags);
      }
    }
  }
}

/** The one instance the registry holds. */
export const currentFlow = new CurrentFlowPiece();
